/**
 * UsageCard — the one card used by every usage card on the dashboard and the Health tab
 * (SF-5124). The title is 16px/600 with an optional grey subtitle under it; the header grows to
 * fit the subtitle and keeps the title at the same top padding, so titles in one row share a
 * baseline. The card fills its column's height, so cards side by side match.
 */
import { Card, Typography, theme } from 'antd';

const { Text } = Typography;

export interface UsageCardProps {
  title: string;
  /** Optional muted line under the title (e.g. the rule a list follows or the range shown). */
  subtitle?: React.ReactNode;
  /** Optional control on the right of the header. */
  extra?: React.ReactNode;
  /** Gives the card a region role named by its title (on by default). */
  region?: boolean;
  /** Body style (e.g. a fixed chart height). */
  bodyStyle?: React.CSSProperties;
  children?: React.ReactNode;
}

/** The title and optional subtitle, as every usage card shows them. */
export const UsageCardTitle: React.FC<{ title: string; subtitle?: React.ReactNode }> = ({
  title,
  subtitle,
}) => {
  const { token } = theme.useToken();
  return (
    <div style={{ whiteSpace: 'normal', minWidth: 0 }}>
      <div style={{ fontSize: 16, fontWeight: 600, lineHeight: '24px' }}>{title}</div>
      {subtitle && (
        <Text
          data-testid="usage-card-subtitle"
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 400,
            lineHeight: '20px',
            color: token.colorTextSecondary,
          }}
        >
          {subtitle}
        </Text>
      )}
    </div>
  );
};

export const UsageCard: React.FC<UsageCardProps> = ({
  title,
  subtitle,
  extra,
  region = true,
  bodyStyle,
  children,
}) => (
  <Card
    title={<UsageCardTitle title={title} subtitle={subtitle} />}
    extra={extra}
    style={{ height: '100%' }}
    styles={{
      // The title sits 16px from the top whether or not a subtitle follows; the header only
      // grows downwards, so neighbouring titles line up.
      header: {
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        paddingTop: 16,
        paddingBottom: subtitle ? 12 : 16,
        minHeight: 56,
      },
      body: bodyStyle,
    }}
    {...(region ? { role: 'region', 'aria-label': title } : {})}
    data-testid="usage-card"
  >
    {children}
  </Card>
);
